/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {login, requestPasswordReset, getUserByToken} from '../core/_requests'
import {useAuth} from '../core/Auth';
import { UserModel } from '../core/_models'
import * as authHelper from '../core/AuthHelpers'
import {PasswordResetConfirmModal} from './modals/PasswordResetConfirmModal'
import {PasswordResetSuccessModal} from './modals/PasswordResetSuccessModal'

const loginSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Username is required'),

  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  username: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [usernameError, setUsernameError] = useState('')
  const [resetLoading, setResetLoading] = useState(false)

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.username, values.password)
        
        // Save auth tokens
        saveAuth(auth)

        // Fetch full user profile including accessPermissions
        try {
          const {data: fullUser} = await getUserByToken(auth.accessToken)
          if (fullUser) {
            setCurrentUser(fullUser)
            authHelper.setUser(fullUser)
          }
        } catch {
          // Fall back to basic user object if profile fetch fails
          const user: UserModel = {
            id: 0,
            username: auth.userName || values.username,
            email: '',
            password: undefined,
            first_name: auth.userName || values.username,
            last_name: ''
          }
          setCurrentUser(user)
          authHelper.setUser(user)
        }
        
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleResetPasswordClick = () => {
    const username = formik.values.username
    
    // Validate username
    if (!username || username.trim() === '') {
      setUsernameError('Username is required')
      return
    }
    
    setUsernameError('')
    setShowConfirmModal(true)
  }

  const handleResetConfirm = async () => {
    // setShowConfirmModal(false)
    setResetLoading(true)
    
    try {
      await requestPasswordReset(formik.values.username)
      setResetLoading(false)
    setShowConfirmModal(false)
      setShowSuccessModal(true)
    } catch (error) {
      setResetLoading(false)
      console.error('Password reset request failed:', error)
      alert('Failed to send password reset request. Please try again.')
    }
  }

  const handleResetCancel = () => {
    setShowConfirmModal(false)
  }

  const handleSuccessClose = () => {
    setShowSuccessModal(false)
  }

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >

      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : ''}

      {/* begin::Form group */}
      <div className='fv-row mb-6'>
        <label className='form-label fs-6 fw-bold text-dark'>Username</label>
        <input
          placeholder='Username'
          {...formik.getFieldProps('username')}
          className={clsx(
            'form-control form-control-lg',
            {'is-invalid': formik.touched.username && formik.errors.username},
            {
              'is-valid': formik.touched.username && !formik.errors.username,
            }
          )}
          type='text'
          name='username'
          autoComplete='off'
        />
        {formik.touched.username && formik.errors.username && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.username}</span>
          </div>
        )}
        {usernameError && (
          <div className='fv-plugins-message-container'>
            <span role='alert' style={{color: '#dc3545'}}>{usernameError}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bold text-dark fs-6 mb-0'>Password</label>
        <input
          type='password'
          autoComplete='off'
          placeholder='Password'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control form-control-lg',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='d-flex justify-content-end mb-8'>
        {/* begin::Link */}
        <button 
          type='button'
          onClick={handleResetPasswordClick}
          className='text-primary fw-semibold fs-6'
          style={{background: 'none', border: 'none', cursor: 'pointer', padding: 0}}
        >
         Reset/Forgot Password ?
        </button>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-5'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary btn-lg'
          disabled={formik.isSubmitting || !formik.isValid}
          style={{
            backgroundColor: '#00a7e1',
            borderColor: '#00a7e1',
            borderRadius: '8px'
          }}
        >
          {!loading && <span className='indicator-label'>Continue</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

    </form>

    <PasswordResetConfirmModal 
      show={showConfirmModal}
      onConfirm={handleResetConfirm}
      onCancel={handleResetCancel}
      loading={resetLoading}
    />

    <PasswordResetSuccessModal 
      show={showSuccessModal}
      onClose={handleSuccessClose}
    />
    </>
  )
}
